import { useRef, useState } from 'react'
import { useDockWidth } from '../shell/dockWidth'

/**
 * The drawer's GEOMETRY — dock width on the panes, and the Source Control divider.
 * Split out of FileBrowser.tsx in M6 (拆面板, 2026-09-03), moved verbatim: none of
 * this knows anything about files, it is the physics of the drawer itself.
 */
export function useGitDock() {
  // Dock width: the panes read --fs-dock and shrink by it (shell/dockWidth.ts — the session's whiteboard docks the same way).
  useDockWidth('min(86vw, 340px)')

  /**
   * How tall Source Control is, as a FRACTION of the drawer.
   *
   * A fraction rather than pixels: the drawer is 340px wide on a desktop and full
   * width on a phone, and its height changes with the keyboard and with rotation. A
   * remembered pixel height that was half of a tall window is nearly all of a short
   * one, and the panel would open having eaten the listing.
   *
   * Clamped to leave both halves usable — a divider that can be dragged to zero is a
   * way to hide a panel you then cannot find.
   */
  const [gitFrac, setGitFrac] = useState<number>(() => {
    const saved = Number(localStorage.getItem('wt_fs_git_frac'))
    return Number.isFinite(saved) && saved >= 0.2 && saved <= 0.8 ? saved : 0.5
  })
  const dockRef = useRef<HTMLElement>(null)

  /**
   * Drag the divider. Pointer events, captured, so it keeps tracking off the element.
   *
   * Without capture the drag dies the moment the cursor outruns a 6px strip, which on
   * a fast drag is immediately — the panel stops following and reads as a divider that
   * does not move rather than one that lost the pointer.
   */
  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const el = e.currentTarget
    el.setPointerCapture(e.pointerId)
    const box = dockRef.current?.getBoundingClientRect()
    if (!box) return
    const move = (ev: PointerEvent) => {
      const frac = (box.bottom - ev.clientY) / box.height
      setGitFrac(Math.min(0.8, Math.max(0.2, frac)))
    }
    const done = () => {
      el.releasePointerCapture(e.pointerId)
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', done)
      el.removeEventListener('pointercancel', done)
      setGitFrac((f) => {
        try { localStorage.setItem('wt_fs_git_frac', String(f)) } catch { /* not fatal */ }
        return f
      })
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', done)
    el.addEventListener('pointercancel', done)
  }

  return { gitFrac, dockRef, startDrag }
}
